import { CONFIG } from "../config";
import type { Hand, NoteEvent, Song } from "../types";

// Pure schematic-staff geometry: pitch spelling, key inference, pitch→staff
// position, wrapped systems, and the Flow/Page camera. No Canvas, no engine.

/** Key signature as a position on the line of fifths: +sharps / -flats, -7..7. */
export interface KeySig {
  fifths: number;
}

/** Diatonic letter 0..6 (C..B) plus any accidental to draw. */
export type Accidental = "sharp" | "flat" | "natural" | null;
interface Spelling {
  /** 0=C … 6=B. */
  letter: number;
  accidental: Accidental;
}

// Per-pitch-class letter + alteration, choosing sharps or flats.
const SHARP: ReadonlyArray<readonly [number, Accidental]> = [
  [0, null],
  [0, "sharp"],
  [1, null],
  [1, "sharp"],
  [2, null],
  [3, null],
  [3, "sharp"],
  [4, null],
  [4, "sharp"],
  [5, null],
  [5, "sharp"],
  [6, null],
];
const FLAT: ReadonlyArray<readonly [number, Accidental]> = [
  [0, null],
  [1, "flat"],
  [1, null],
  [2, "flat"],
  [2, null],
  [3, null],
  [4, "flat"],
  [4, null],
  [5, "flat"],
  [5, null],
  [6, "flat"],
  [6, null],
];
// Letters altered by a key signature, in order of accidentals added.
const SHARP_ORDER = [3, 0, 4, 1, 5, 2, 6]; // F C G D A E B
const FLAT_ORDER = [6, 2, 5, 1, 4, 0, 3]; // B E A D G C F

/** Spell a pitch class (0..11) under a key signature → letter + accidental glyph. */
export function spell(pc: number, fifths: number): Spelling {
  const table = fifths >= 0 ? SHARP : FLAT;
  const entry = table[((pc % 12) + 12) % 12];
  if (!entry) return { letter: 0, accidental: null };
  const [letter, accidental] = entry;
  // Letters the key signature already alters (so we omit the redundant glyph).
  const altered = new Set(
    fifths >= 0 ? SHARP_ORDER.slice(0, fifths) : FLAT_ORDER.slice(0, -fifths),
  );
  if (accidental !== null && altered.has(letter)) return { letter, accidental: null };
  // A natural that contradicts the key signature needs an explicit ♮.
  if (accidental === null && altered.has(letter)) return { letter, accidental: "natural" };
  return { letter, accidental };
}

// Krumhansl-Kessler tonal-hierarchy profiles.
const MAJOR_PROFILE = [6.35, 2.23, 3.48, 2.33, 4.38, 4.09, 2.52, 5.19, 2.39, 3.66, 2.29, 2.88];
const MINOR_PROFILE = [6.33, 2.68, 3.52, 5.38, 2.6, 3.53, 2.54, 4.75, 3.98, 2.69, 3.34, 3.17];
// Major-key tonic pitch class → fifths (C=0, G=1 … F=-1 …), in -7..7.
const PC_TO_FIFTHS = [0, -5, 2, -3, 4, -1, 6, 1, -4, 3, -2, 5];

function correlate(hist: number[], profile: number[], shift: number): number {
  const meanH = hist.reduce((a, b) => a + b, 0) / 12;
  const meanP = profile.reduce((a, b) => a + b, 0) / 12;
  let num = 0;
  let dH = 0;
  let dP = 0;
  for (let i = 0; i < 12; i++) {
    const h = (hist[(i + shift) % 12] ?? 0) - meanH;
    const p = (profile[i] ?? 0) - meanP;
    num += h * p;
    dH += h * h;
    dP += p * p;
  }
  const denom = Math.sqrt(dH * dP);
  return denom === 0 ? -2 : num / denom;
}

/** Infer a key signature from a note set (duration-weighted Krumhansl-Schmuckler). */
export function keyFromNotes(notes: readonly NoteEvent[]): KeySig {
  const hist = new Array(12).fill(0) as number[];
  for (const n of notes) {
    const pc = ((n.midi % 12) + 12) % 12;
    hist[pc] = (hist[pc] ?? 0) + Math.max(0.01, n.duration);
  }
  if (notes.length === 0) return { fifths: 0 };
  let bestTonic = 0;
  let bestScore = Number.NEGATIVE_INFINITY;
  for (let tonic = 0; tonic < 12; tonic++) {
    const maj = correlate(hist, MAJOR_PROFILE, tonic);
    const min = correlate(hist, MINOR_PROFILE, tonic);
    // Minor keys spell from their relative major (tonic + 3 semitones).
    const score = Math.max(maj, min);
    const fifthsTonic = maj >= min ? tonic : (tonic + 3) % 12;
    if (score > bestScore) {
      bestScore = score;
      bestTonic = fifthsTonic;
    }
  }
  return { fifths: PC_TO_FIFTHS[bestTonic] ?? 0 };
}

export type Clef = "treble" | "bass";

interface StaffPos {
  clef: Clef;
  /** Diatonic steps from middle C (C4 = 0); +1 per line-or-space upward. */
  step: number;
  accidental: Accidental;
}

// Treble middle line = B4 (step 6); bass middle line = D3 (step -6).
const CLEF_MIDDLE_STEP: Record<Clef, number> = { treble: 6, bass: -6 };

/** Pitch + assigned hand → clef, diatonic step, and accidental glyph. */
export function staffPos(note: NoteEvent, key: KeySig, hand: Hand): StaffPos {
  const clef: Clef =
    hand === "right" ? "treble" : hand === "left" ? "bass" : note.midi >= 60 ? "treble" : "bass";
  const spelling = spell(((note.midi % 12) + 12) % 12, key.fifths);
  const octave = Math.floor(note.midi / 12) - 1; // MIDI 60 = C4
  const step = (octave - 4) * 7 + spelling.letter;
  return { clef, step, accidental: spelling.accidental };
}

/**
 * Y (px, canvas-down) of a diatonic `step` for a clef, given the clef's middle-line Y.
 * Each step is half a line-gap; higher steps → smaller Y.
 */
export function staffStepY(clef: Clef, step: number, middleLineY: number): number {
  return middleLineY - (step - CLEF_MIDDLE_STEP[clef]) * (CONFIG.staff.lineGapPx / 2);
}

interface System {
  index: number;
  barStart: number;
  barEnd: number;
  tStart: number;
  tEnd: number;
}

interface StaffLayout {
  key: KeySig;
  systems: System[];
  /** System index containing `t` (clamped to [0, last]). */
  systemOf(t: number): number;
  /** 0..1 position of `t` within its own system (time-proportional). */
  xFrac(t: number): number;
}

/** Build the wrapped, whole-song layout (pure; cache via `staffLayoutFor`). */
export function buildStaffLayout(song: Song, barsPerSystem: number, key: KeySig): StaffLayout {
  const bars = song.bars;
  const barCount = Math.max(0, bars.length - 1);
  const systems: System[] = [];
  for (let b = 0; b < barCount; b += barsPerSystem) {
    const barEnd = Math.min(b + barsPerSystem, barCount);
    systems.push({
      index: systems.length,
      barStart: b,
      barEnd,
      tStart: bars[b] ?? 0,
      tEnd: bars[barEnd] ?? song.duration,
    });
  }
  if (systems.length === 0) {
    systems.push({ index: 0, barStart: 0, barEnd: 0, tStart: 0, tEnd: song.duration || 1 });
  }
  const systemOf = (t: number): number => {
    for (let i = systems.length - 1; i >= 0; i--) {
      const s = systems[i];
      if (s && t >= s.tStart) return i;
    }
    return 0;
  };
  const xFrac = (t: number): number => {
    const s = systems[systemOf(t)];
    if (!s) return 0;
    const span = s.tEnd - s.tStart;
    return span > 0 ? Math.max(0, Math.min(1, (t - s.tStart) / span)) : 0;
  };
  return { key, systems, systemOf, xFrac };
}

/** Flow mode: screen x of a note at song-time `t` given the playhead and staff width. */
export function flowNoteX(t: number, playhead: number, width: number): number {
  const nowLine = width * CONFIG.staff.nowLineFraction;
  const pxPerSec = (width * (1 - CONFIG.staff.nowLineFraction)) / CONFIG.staff.flowLookaheadSeconds;
  return nowLine + (t - playhead) * pxPerSec;
}

/** Page mode visible-system window. `rows<=0` = fit (show all from 0). */
export function pageVisible(
  currentSystem: number,
  rows: number,
  total: number,
): { first: number; count: number } {
  if (rows <= 0) return { first: 0, count: total };
  const page = Math.floor(currentSystem / rows);
  return { first: page * rows, count: Math.min(rows, total - page * rows) };
}

// Layout only changes on song / barsPerSystem / key — memo one (single-song app).
let cached: { song: Song; bps: number; fifths: number; layout: StaffLayout } | null = null;
export function staffLayoutFor(song: Song, barsPerSystem: number, key: KeySig): StaffLayout {
  const c = cached;
  if (c && c.song === song && c.bps === barsPerSystem && c.fifths === key.fifths) return c.layout;
  const layout = buildStaffLayout(song, barsPerSystem, key);
  cached = { song, bps: barsPerSystem, fifths: key.fifths, layout };
  return layout;
}
